import { Box, Typography, Dialog, DialogTitle, DialogContent, IconButton, InputAdornment, TextField, Button, Chip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import Header from "../../../components/Header"
import { palette } from "../../../theme";
import { useEffect, useMemo, useState } from "react";
import ImageProfile from "../../../components/imageProfile";
import API from "../../../api";
import { useNavigate } from "react-router-dom";
import { ReactNotifications } from "react-notifications-component";
import { updateToastError, startToast, updateToastSuccess } from "../../../utils/notification-loading";
import { MUTED, SURFACE, SURFACE_HEAD, BORDER } from "../../dashboard/dashboard-components/resellerUi";

const GRID = "repeat(auto-fill, minmax(320px, 1fr))";

// Cerchio con iniziale; se il cliente ha un'immagine valida ImageProfile la sovrappone.
const CustomerAvatar = ({ name, base64Data, format, size = 52 }) => (
  <Box
    sx={{
      position: "relative",
      flexShrink: 0,
      width: size,
      height: size,
      borderRadius: "50%",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: palette.primary.light,
      backgroundColor: "rgba(69,144,149,0.14)",
      fontWeight: 700,
      fontSize: size * 0.4,
    }}
  >
    {(name || "?").trim().charAt(0).toUpperCase()}
    <ImageProfile
      base64Data={base64Data}
      format={format}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
    />
  </Box>
);

// Riga informativa con icona (e tooltip nativo) sotto il nome del cliente.
const InfoRow = ({ icon: Icon, title, children }) => (
  <Box title={title} sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, color: MUTED }}>
    <Icon sx={{ fontSize: 15, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 13 }} noWrap>{children}</Typography>
  </Box>
);

// Il server può restituire date preformattate non valide ("NaN/NaN/NaN ...").
const safeDate = (v) => (!v || /NaN/.test(String(v)) ? "-" : v);

const daysColor = (d) => {
  const n = Number(d);
  if (!Number.isFinite(n)) return "#eaeaea";
  return n <= 0 ? palette.error.light : n <= 30 ? "#EF9F27" : "#eaeaea";
};

const Customers = ({ setCustomerName, setTimeZone, setLecsInfo }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState();
  const [open, setOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState([]);
  const [loadingLecs, setLoadingLecs] = useState(false);
  const [selectedRS, setSelectedRS] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const commercial = sessionStorage.getItem("commercial")

  useEffect(() => {
    API.getCustomers().then(response => {
      setCustomers(response.data)
    }).catch(error => {
      console.error('[Customers] Failed to fetch customers:', error)
      setCustomers([])
    })
  }, [])

  const handleCloseDialog = () => {
    setOpen(false)
    setSelectedCustomer([])
  }

  const handleClickCustomer = (email, ragioneSociale) => {
    setSelectedRS(ragioneSociale)
    setSelectedCustomer([])
    setLoadingLecs(true)
    API.getCustomers({ email: email }).then(response => {
      setSelectedCustomer(response.data)
    }).catch((err) => {
      console.error('[handleClickCustomer] Failed to fetch customer:', err)
    }).finally(() => setLoadingLecs(false))
    setOpen(true)
  }

  // Seleziona il LECS come customer corrente e apre la pagina richiesta.
  const enterLecs = (item, to) => {
    startToast();
    API.postCurrentCustomer({ serial_number: item.matricola }).then(response => {
      setCustomerName(selectedRS + ' ' + item.matricola)
      API.getTimeZone().then(res => { sessionStorage.setItem("timezone", res.data); setTimeZone(res.data) }).catch(error => { console.error('[Customers] Failed to fetch timezone:', error) })
      API.getLecsStatus().then(res => { setLecsInfo(res.data) }).catch(error => { console.error('[Customers] Failed to fetch LECS status:', error) })
      window.sessionStorage.setItem("tipoLecs", response.data.tipoLecs);
      updateToastSuccess("Ok");
      navigate(to)
    }).catch(error => {
      console.error('[Customers] Failed to set current customer:', error)
      updateToastError(`You are not authorized to view user's LECS ${selectedRS}, please request permission to continue`);
    })
  }

  // Filtra i clienti basandoti sulla ricerca
  const filteredCustomers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const nameOf = (c) => (c.ragioneSociale || c.nome || "").trim();
    // Sempre ordinati per nome (case/accent-insensitive, numeri in ordine naturale), anche durante la ricerca.
    return [...(customers ?? [])].sort((a, b) =>
      nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base", numeric: true })
    ).filter(c =>
      !q ||
      c.ragioneSociale?.toLowerCase().includes(q) ||
      c.nome?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  }, [customers, searchTerm]);

  if (commercial === "true") return <Typography>Not authorized</Typography>;

  const toolbar = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
      <TextField
        size="small"
        placeholder="Search by name or email…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ minWidth: 260, flex: 1, maxWidth: 420 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: MUTED }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <Chip
        size="small"
        label={`${filteredCustomers.length}${customers && filteredCustomers.length !== customers.length ? ` / ${customers.length}` : ""} customers`}
        sx={{ color: MUTED, backgroundColor: "transparent", border: "1px solid rgba(255,255,255,0.14)" }}
      />
    </Box>
  );

  return (
    <>
      <Dialog
        open={open} onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: {
            sx: { backgroundColor: SURFACE, backgroundImage: "none", border: `1px solid ${BORDER}`, borderRadius: "10px" },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 1.25,
            backgroundColor: SURFACE_HEAD,
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          <Box component="span" sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>LECS of </Box>
            {selectedRS}
          </Box>
          <IconButton size="small" onClick={handleCloseDialog} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <ReactNotifications />
          {!loadingLecs && selectedCustomer.length === 0 ? (
            <Typography sx={{ color: "orange" }}>
              You are not authorized to view {selectedRS}'s LECS, please request permission to continue.
            </Typography>
          ) : (
            <Typography sx={{ color: MUTED, mb: 2 }}>
              {loadingLecs ? "Loading…" : "Choose where to go for each LECS."}
            </Typography>
          )}

          {selectedCustomer.length > 0 && (
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px", overflow: "hidden" }}>
              {selectedCustomer.map((item, i) => (
                <Box
                  key={item.matricola}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    borderTop: i === 0 ? "none" : `1px solid ${BORDER}`,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap>
                      <Box component="span" sx={{ fontWeight: 700, color: palette.primary.light }}>{item.matricola}</Box>
                      {item.nomeLecs && <Box component="span" sx={{ ml: 1, fontWeight: 600 }}>{item.nomeLecs}</Box>}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: MUTED }} noWrap>
                      {item.dipartimentoSede || "-"}
                      {" · "}
                      <Box component="span" sx={{ color: daysColor(item.giorniRimanenti), fontWeight: 600 }}>
                        {item.giorniRimanenti ?? "-"} days left
                      </Box>
                      {" · Activated "}
                      {safeDate(item.dataAttivazione)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                    <Button variant="contained" size="small" onClick={() => enterLecs(item, '/reseller/myC/dashboard')}>
                      Dashboard
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => enterLecs(item, '/reseller/myC/report')}
                      sx={{ color: "#eaeaea", borderColor: "rgba(255,255,255,0.14)", "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" } }}
                    >
                      Report
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Box m="20px">
        <Box mb={2}>
          <Header title="CUSTOMERS MANAGEMENT" />
        </Box>

        <Box mb={2}>{toolbar}</Box>

        {customers === undefined ? (
          <Typography sx={{ color: MUTED }}>Loading…</Typography>
        ) : filteredCustomers.length === 0 ? (
          <Typography sx={{ color: MUTED }}>No customers found.</Typography>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: GRID, gap: 2 }}>
            {filteredCustomers.map((customer, index) => {
              const onOpen = () => handleClickCustomer(customer.email, customer.ragioneSociale);
              return (
                <Box
                  key={customer.email ?? index}
                  role="button"
                  tabIndex={0}
                  onClick={onOpen}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    backgroundColor: SURFACE,
                    border: `1px solid ${BORDER}`,
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "border-color .15s ease, background-color .15s ease",
                    "&:hover": { borderColor: palette.primary.main, backgroundColor: "#222" },
                    "&:focus-visible": { outline: `1px solid ${palette.primary.light}`, outlineOffset: 2 },
                  }}
                >
                  <CustomerAvatar name={customer.ragioneSociale} base64Data={customer.profileImage} format={customer.imageType} />
                  <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Typography sx={{ fontWeight: 700, mb: 0.25 }} noWrap>{customer.ragioneSociale || "—"}</Typography>
                    {customer.email && <InfoRow icon={MailOutlineIcon} title="Email">{customer.email}</InfoRow>}
                    {customer.telefono && <InfoRow icon={PhoneOutlinedIcon} title="Phone">{customer.telefono}</InfoRow>}
                    {customer.admin && <InfoRow icon={ManageAccountsOutlinedIcon} title="Admin">{customer.admin}</InfoRow>}
                  </Box>
                  <ChevronRightIcon sx={{ flexShrink: 0, color: MUTED }} />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </>
  )
}

export default Customers
